import { request } from '@umijs/max';
import type {
  ProjectSettingsResponse,
  UpdateProjectSettingsRequest,
} from '@/types/projectSettings';
import { assertSuccess } from '@/utils/apiHelpers';
import { getErrorCode, getErrorStatus, isBackendError } from '@/utils/errors';
import { createApiLogContext, createLogger } from '@/utils/logger';
import type {
  CreatedProject,
  CreateProjectRequest,
  InvitationLinkResponse,
  InviteProjectAdminRequest,
  OrgProjectsDashboardResponse,
  ProjectDetail,
  RegisteredProjectAdminResponse,
  UserOrganization,
} from './types';

const logger = createLogger('OrgAdminService');

// ============================================================================
// 【中心管理】Center Management
// ============================================================================

/**
 * 获取当前用户参与的组织列表（包含角色信息）
 */
export async function getMyCenters(): Promise<{
  success: boolean;
  data: UserOrganization[];
}> {
  const logCtx = createApiLogContext({ action: '获取组织列表', module: 'OrgAdminService' });
  try {
    const response = await request<{
      status: string;
      data: UserOrganization[];
      message: string;
      code: string;
      timestamp: string;
    }>('/v1/users/me/organizations', {
      method: 'GET',
      __logContext: logCtx,
    });

    assertSuccess(response, '获取组织列表失败');

    logger.info('获取组织列表成功', logCtx);
    return {
      success: true,
      data: response.data || [],
    };
  } catch (error) {
    logger.error('获取组织列表失败', error, logCtx);
    throw error;
  }
}

/**
 * 获取组织项目列表面板数据（新版聚合接口）
 * 一次调用即可获取：
 * 1. 组织级统计信息（项目总数、项目总人数）
 * 2. 项目列表（包含每个项目的详细统计）
 * 3. 分页信息
 *
 * @param organizationId - 组织ID
 * @param options - 可选参数
 * @returns 组织项目面板数据
 */
export async function getOrgProjectsDashboard(
  organizationId: string,
  options?: {
    skip?: number;
    limit?: number;
    status?: 'active' | 'inactive' | 'deprecated';
    require_fresh?: boolean;
  },
): Promise<{
  success: boolean;
  data: OrgProjectsDashboardResponse;
}> {
  const params: Record<string, any> = {
    skip: options?.skip ?? 0,
    limit: options?.limit ?? 100, // 默认获取所有项目
  };

  if (options?.status) {
    params.status = options.status;
  }
  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }

  const logCtx = createApiLogContext({ action: '获取组织项目面板数据', module: 'OrgAdminService', organizationId });
  try {
    const response = await request<{
      status: string;
      data: OrgProjectsDashboardResponse;
      message: string;
      code?: string;
      timestamp: string;
      trace_id?: string;
    }>(`/v1/workspace/dashboard/org/${organizationId}/projects`, {
      method: 'GET',
      params,
      __logContext: logCtx,
    });

    assertSuccess(response, '获取组织项目面板数据失败');

    logger.info('获取组织项目面板数据成功', logCtx);

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logger.error('获取组织项目面板数据失败', error, logCtx);
    throw error;
  }
}

/**
 * 获取组织已注册项目管理员列表面板数据（新版聚合接口）
 * 后端已完成：
 * 1. 按用户聚合（同一用户管理多个项目会合并为一条记录）
 * 2. 授权项目列表整合
 * 3. 最早加入时间计算
 * 4. 手机号自动脱敏
 *
 * @param organizationId - 组织ID
 * @param options - 可选参数
 * @returns 已注册项目管理员列表
 */
export async function getOrgRegisteredAdmins(
  organizationId: string,
  options?: {
    skip?: number;
    limit?: number;
    require_fresh?: boolean;
  },
): Promise<{
  success: boolean;
  data: RegisteredProjectAdminResponse;
}> {
  const params: Record<string, any> = {
    skip: options?.skip ?? 0,
    limit: options?.limit ?? 100, // 默认获取所有管理员
  };

  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }

  const logCtx = createApiLogContext({ action: '获取已注册项目管理员列表', module: 'OrgAdminService', organizationId });
  try {
    const response = await request<{
      status: string;
      data: RegisteredProjectAdminResponse;
      message: string;
      code?: string;
      timestamp: string;
      trace_id?: string;
    }>(`/v1/workspace/dashboard/org/${organizationId}/admins/registered`, {
      method: 'GET',
      params,
      __logContext: logCtx,
    });

    assertSuccess(response, '获取已注册项目管理员列表失败');

    logger.info('获取已注册项目管理员列表成功', logCtx);

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logger.error('获取已注册项目管理员列表失败', error, logCtx);
    throw error;
  }
}

// ============================================================================
// 【项目管理】Project Management
// ============================================================================

/**
 * 获取组织项目列表（用于其他组件独立调用，如项目详情页下拉菜单）
 * 注意：主工作台页面请使用 getOrgProjectsDashboard 替代
 */
export async function getCenterProjects(
  organizationId: string,
  options?: {
    skip?: number;
    limit?: number;
    status?: 'active' | 'inactive' | 'deprecated';
    require_fresh?: boolean;
  },
): Promise<{
  success: boolean;
  data: any[];
}> {
  const params: Record<string, any> = {};
  if (options?.skip !== undefined) {
    params.skip = options.skip;
  }
  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  if (options?.status) {
    params.status = options.status;
  }
  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }

  const logCtx = createApiLogContext({ action: '获取项目列表', module: 'OrgAdminService', organizationId });
  try {
    const response = await request<{
      status: string;
      data: {
        items: ProjectDetail[];
        pagination: {
          total_items: number;
          total_pages: number;
          page: number;
          page_size: number;
        };
      };
      message: string;
      code: string;
      timestamp: string;
      trace_id?: string;
    }>(`/v1/orgs/${organizationId}/projects`, {
      method: 'GET',
      params,
      __logContext: logCtx,
    });

    assertSuccess(response, '获取项目列表失败');

    logger.info('获取项目列表成功', logCtx);

    // 简单转换格式
    const projects = (response.data?.items || []).map((project) => ({
      project_id: project.id,
      project_name: project.name,
      project_type: project.project_type,
      project_status: project.status,
      organization_id: project.organization_id,
      organization_name: '',
      roles: [],
      primary_role: '',
      staff_title: '',
      assigned_at: project.created_at,
      created_at: project.created_at,
      updated_at: project.updated_at,
    }));

    return {
      success: true,
      data: projects,
    };
  } catch (error) {
    logger.error('获取项目列表失败', error, logCtx);
    throw error;
  }
}

// ============================================================================
// 【管理员管理】Admin Management
// ============================================================================

/**
 * 获取项目管理员列表（用于其他组件独立调用，如邀请模态框）
 * 注意：主工作台页面请使用 getOrgRegisteredAdmins 替代
 */
export async function getProjectAdmins(projectId: number): Promise<{
  success: boolean;
  data: any[];
}> {
  const logCtx = createApiLogContext({ action: '获取项目管理员列表', module: 'OrgAdminService', projectId });
  try {
    const response = await request<{
      status: string;
      data: any[];
      message: string;
      code: string;
      timestamp: string;
    }>(`/v1/projects/${projectId}/admins`, {
      method: 'GET',
      __logContext: logCtx,
    });

    assertSuccess(response, '获取项目管理员列表失败');

    const data = response.data || [];
    logger.info('获取项目管理员列表成功', logCtx);

    return {
      success: true,
      data,
    };
  } catch (error) {
    logger.error('获取项目管理员列表失败', error, logCtx);
    throw error;
  }
}

/**
 * 为组织创建项目
 * project_type 始终为 general；settings、management_cycle_months、start_date 来自创建弹窗
 */
export async function createProject(
  organizationId: string,
  params: CreateProjectRequest,
): Promise<{
  success: boolean;
  data: CreatedProject;
}> {
  const requestBody = {
    name: params.name,
    project_type: 'general' as const,
    description: params.description ?? params.name,
    management_cycle_months: params.management_cycle_months ?? 24,
    inclusion_criteria: params.inclusion_criteria,
    exclusion_criteria: params.exclusion_criteria,
    ...(params.start_date != null ? { start_date: params.start_date } : {}),
    ...(params.settings != null ? { settings: params.settings } : {}),
  };

  const logCtx = createApiLogContext({ action: '创建项目', module: 'OrgAdminService', organizationId });
  try {
    const response = await request<{
      status: string;
      data: CreatedProject;
      message: string;
      code: string;
      timestamp: string;
      trace_id?: string;
    }>(`/v1/orgs/${organizationId}/projects`, {
      method: 'POST',
      data: requestBody,
      __logContext: logCtx,
    });

    assertSuccess(response, '创建项目失败');

    logger.info('创建项目成功', logCtx);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logger.error('创建项目失败', error, logCtx);
    throw error;
  }
}

/**
 * 获取项目详情
 */
export async function getProject(projectId: number): Promise<{
  success: boolean;
  data: ProjectDetail;
}> {
  const logCtx = createApiLogContext({ action: '获取项目详情', module: 'OrgAdminService', projectId });
  try {
    const response = await request<{
      status: string;
      data: ProjectDetail;
      message: string;
      code: string;
      timestamp: string;
    }>(`/v1/projects/${projectId}`, {
      method: 'GET',
      __logContext: logCtx,
    });

    assertSuccess(response, '获取项目详情失败');

    logger.info('获取项目详情成功', logCtx);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logger.error('获取项目详情失败', error, logCtx);
    throw error;
  }
}

// ============================================================================
// 【项目设置】Project Settings（GET/PUT）
// ============================================================================

/**
 * 获取项目设置
 * GET /v1/projects/{project_id}/settings
 * 组织管理员用于查看/编辑，患者相关用于展示或业务逻辑
 */
export async function getProjectSettings(
  projectId: number,
  options?: { require_fresh?: boolean },
): Promise<{ success: boolean; data: ProjectSettingsResponse }> {
  const params: Record<string, unknown> = {};
  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }
  const response = await request<{
    status: string;
    data: ProjectSettingsResponse;
    message?: string;
    code?: string;
    timestamp?: string;
    trace_id?: string;
  }>(`/v1/projects/${projectId}/settings`, {
    method: 'GET',
    params: Object.keys(params).length ? params : undefined,
  });

  assertSuccess(response, '获取项目设置失败');
  return {
    success: true,
    data: response.data,
  };
}

/**
 * 更新项目设置
 * PUT /v1/projects/{project_id}/settings
 * 全量更新：请求体即最终配置，整体覆盖，不做合并。management、modules 必填，modules 须包含全部子模块。
 */
export async function updateProjectSettings(
  projectId: number,
  body: UpdateProjectSettingsRequest,
  options?: { require_fresh?: boolean },
): Promise<{ success: boolean; data: ProjectSettingsResponse }> {
  const params: Record<string, unknown> = {};
  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }
  const logCtx = createApiLogContext({ action: '更新项目设置', module: 'OrgAdminService', projectId });
  try {
    const response = await request<{
      status: string;
      data: ProjectSettingsResponse;
      message?: string;
      code?: string;
      timestamp?: string;
      trace_id?: string;
    }>(`/v1/projects/${projectId}/settings`, {
      method: 'PUT',
      data: body,
      params: Object.keys(params).length ? params : undefined,
      __logContext: logCtx,
    });

    assertSuccess(response, '更新项目设置失败');
    logger.info('更新项目设置成功', logCtx);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logger.error('更新项目设置失败', error, logCtx);
    throw error;
  }
}

// ============================================================================
// 【邀请管理】Invitation Management
// ============================================================================

/**
 * 生成项目管理员邀请链接
 */
export async function generateProjectAdminInvitation(
  centerId: string,
  params: InviteProjectAdminRequest,
): Promise<{
  success: boolean;
  data: InvitationLinkResponse;
}> {
  const logCtx = createApiLogContext({
    action: '创建邀请',
    module: 'OrgAdminService',
    organizationId: centerId,
    projectId: params.project_id,
  });
  try {
    // 根据 role_id 验证必需的上下文字段
    // role_id=4: 项目管理员需要 project_id 和 organization_id
    if (params.role_id === 4) {
      if (!params.project_id) {
        throw new Error('项目管理员邀请需要指定 project_id');
      }
      if (!centerId) {
        throw new Error('项目管理员邀请需要指定 organization_id');
      }
    }

    // 将 project_id 从 string 转换为 number
    const project_id = params.project_id
      ? Number(params.project_id)
      : undefined;
    if (project_id !== undefined && Number.isNaN(project_id)) {
      throw new Error('project_id 必须是有效的数字');
    }

    // 构建最终请求数据
    // 请求体格式：{"phone_number": "xxxxxxxxxxx", "full_name": "xxx", "role_id": 4, "organization_id": "uuid", "project_id": 123, "expires_in_days": 7}
    // 其中 project_id 为邀请进入锁定的项目的 id，organization_id 为邀请进入的组织的 id
    const requestData = {
      phone_number: params.phone_number,
      full_name: params.full_name,
      role_id: params.role_id,
      organization_id: centerId,
      project_id,
      expires_in_days: params.expires_in_days,
    };

    const response = await request<{
      status: string;
      data: {
        id: string;
        token: string;
        phone_number: string;
        full_name: string;
        role_id: number;
        organization_id?: string;
        project_id?: number;
        group_id?: string;
        status: string;
        created_at: string;
        expires_at: string;
      };
      message: string;
      code?: string;
      timestamp: string;
    }>(`/v1/invitations`, {
      method: 'POST',
      data: requestData,
      __logContext: logCtx,
    });

    assertSuccess(response, '创建邀请失败');

    logger.info('创建邀请成功', logCtx);

    // 构建邀请链接
    const baseUrl = window.location.origin;
    const invitationLink = `${baseUrl}/invitation?token=${response.data.token}`;

    return {
      success: true,
      data: {
        ...response.data,
        invitation_link: invitationLink,
      },
    };
  } catch (error) {
    const status = getErrorStatus(error);
    const responseData = isBackendError(error)
      ? error.response?.data
      : undefined;

    if (status === 404) {
      logger.error('创建邀请接口不存在', error, logCtx);
      throw new Error('创建邀请接口不存在，请检查后端API路径');
    }

    if (status === 409 || getErrorCode(error) === 'CONFLICT') {
      logger.warn('创建邀请冲突：该手机号已有待处理邀请', logCtx);
      const conflictError = new Error(
        responseData?.message || '该手机号已有待处理的邀请',
      ) as Error & { code?: string };
      conflictError.code = 'CONFLICT';
      throw conflictError;
    }

    logger.error('创建邀请失败', error, logCtx);

    if (responseData?.detail) {
      const detail = responseData.detail;
      if (typeof detail === 'string') {
        throw new Error(detail);
      } else if (Array.isArray(detail) && detail.length > 0) {
        throw new Error(detail[0]?.msg || '数据验证失败');
      }
    }
    if (isBackendError(error) && error.message) {
      throw error;
    }
    throw new Error('创建邀请失败，请检查输入参数');
  }
}
